(ns frontend.view.reports.custom.alj
  (:require
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.bootstrap.components :as rbs]
   [frontend.db :as app-db]
   [frontend.exports.excel :as excel]
   [frontend.state.current-session :as session]
   [frontend.view.components.clipboard :as clipboard]
   [potpuri.core :as p]
   [reagent.core :as r]
   [ui.components.chart.view :as chart]
   [ui.components.card.view :as card]))

(def primary-color "#51859A")

(defn alj-report-pipeline-id
  []
  (->> @session/all-pipelines
       (remove :common/disabled?)
       (cu/filter-by :pipeline/name "ALJ Global")
       (first)
       :db/id))

(defn ^{:jsdoc ["@nocollapse"]} format-data-point
  [_]
  (this-as js-data-point
           (let [data-point (js->clj js-data-point :keywordize-keys true)
                 {:keys [y x]} data-point]
             (if (and (number? y) (not= 0 y))
               y
               ""))))

(defn alj-chart
  [{:keys [view-name total-deals chart-data report-data x-axis-labels]}]
  [card/card
   [card/header
    [:span.title-h3.d-inline-block.pr-2
     view-name
     " "
     [:span.grid-count
      (str (loc/tr :total-deals) ": " total-deals)]]
    [:div.d-flex.justify-content-end
     [:i.fal.fa-copy.mr-3.pointer
      {:title    (loc/tr :copy-to-clipboard)
       :on-click (fn [_]
                   (clipboard/copy-as-tsv report-data))}]
     [:i.fal.fa-file-excel.pointer
      {:title    (loc/tr :save-as-excel)
       :on-click (fn [_]
                   (excel/export-array-data view-name
                                            report-data))}]]]
   [card/body
    [:div.reports-chart-wrapper
     [chart/view
      (chart/options
       {:chart       {:type         "bar"
                      :backgroundColor "#FFFFFF"
                      :borderRadius 10
                      :width        (let [window-width (:width @app-db/window)]
                                      (cond (< window-width 900)
                                            (- window-width 210)

                                            :else
                                            (min (int (/ (- window-width 210) 2))
                                                 1000)
                                      ))}
        :title       nil
        :xAxis       {:categories x-axis-labels}
        :yAxis       {:min             0
                      :stackLabels     {:enabled true}
                      :lineWidth       0
                      :minorTickLength 0
                      :tickLength      0
                      :tickInterval    10
                      :gridLineColor   "#F2F2EE"
                      :title           {:enabled false}
                      :plotLines       [{:value  0
                                         :width  2
                                         :color  "#000"
                                         :zIndex 10}]}
        :series      [{:data chart-data}]
        :credits     {:enabled false}
        :legend      {:enabled false}
        :borderWidth 1
        :shadow      false
        :tooltip     {:headerFormat "<b>{point.x}</b><br/>"
                      :pointFormat  "Active: {point.y}"}
        :plotOptions {:series {:dataLabels {:enabled   true
                                            :formatter format-data-point}}
                      :column {:stacking   "normal"
                               :dataLabels {:enabled       true
                                            :style         {:textOutline 0}
                                            :borderWidth   0
                                            :verticalAlign "top"
                                            :formatter     format-data-point}}}})]]]])

(defn replace-zeros
  [named-count-pairs]
  (->> named-count-pairs
       (map (fn [[n c]]
              [n
               (if (and (number? c)
                        (pos? c))
                 c
                 "-")]))))

(defn alj-table
  [{:keys [view-name header-row data-rows footer-row]}]
  [card/card
   [card/header
    [:div.d-flex.align-items-center
     [:span.title-h3.d-inline-block.pr-2 view-name]]

    [:div.d-flex.justify-content-end
     [:i.fal.fa-copy.mr-3.pointer
      {:title    (loc/tr :copy-to-clipboard)
       :on-click (fn [_]
                   (clipboard/copy-as-tsv (concat [header-row]
                                                  data-rows
                                                  [footer-row])))}]
     [:i.fal.fa-file-excel.pointer
      {:title    (loc/tr :save-as-excel)
       :on-click (fn [_]
                   (excel/export-array-data
                    view-name
                    (concat [header-row] data-rows [footer-row])))}]]]
   [card/body
    [:table.zap-simple-table.zap-simple-table--md.w-100
     [:thead
      [:tr
       (->> header-row
            (map-indexed (fn [idx heading]
                           ^{:key (str view-name "-header-" idx)}
                           [:th heading]))
            (doall))]]
     [:tbody
      {:key (str view-name "-body")}
      (->> data-rows
           (map-indexed
            (fn [row-idx row-data]
              [:tr
               {:key (str view-name "-row-" row-idx)}
               (->> row-data
                    (map-indexed (fn [idx cell-data]
                                   [:td
                                    {:key        (str view-name "-" row-idx "-" idx)
                                     :class-name (if (= 0 idx) "string-value" "number-value")}
                                    cell-data]))
                    (doall))]))
           (doall))]
     [:tfoot
      [:tr
       (->> footer-row
            (map-indexed (fn [idx footer-cell-data]
                           [:td
                            {:key (str view-name "-footer-" idx)}
                            footer-cell-data]))
            (doall))]]]]])

(defn all-deals
  []
  (let [alj-global-pipeline-id (alj-report-pipeline-id)]
    (->> @session/all-oppos
         (remove :common/disabled?)
         (cu/filter-by :oppo/pipeline alj-global-pipeline-id))))

(defn active-deals
  []
  ;; reaction in session ?
  (->> (all-deals)
       (cu/filter-by :oppo/status :oppo.status/ok)))

(defn multi-named-value-counts-report
  [entities attribute pipeline-id]
  (->> (session/non-archived-named-values attribute pipeline-id)
       (map (fn [nv]
              (let [c (->> entities
                           (filter (fn [o]
                                     (some #{(:db/id nv)} (get o attribute))))
                           (count))]
                [(:named-value/value nv) c])))
       (sort-by second)
       (reverse)))                                          ;; desc

(defn deals-per-stages
  [deals]
  (let [report-pipeline-id (alj-report-pipeline-id)]
    (->> @session/all-stages
         (remove :common/disabled?)
         (cu/filter-by :stage/pipeline report-pipeline-id)
         (map (fn [stage]
                (let [c (->> deals
                             (cu/filter-by :oppo/stage (:db/id stage))
                             (count))]
                  [(:stage/name stage) c])))
         (sort-by (fn [[n _]]
                    (cu/str-trim-lcase n)))
         (replace-zeros)
         (doall))))

(defn deals-per-stages-no-zeros
  [deals]
  (->> deals
       (deals-per-stages)
       (replace-zeros)
       (doall)))

(defn active-deals-per-stage
  []
  (let [active-deals (active-deals)
        report-data (deals-per-stages active-deals)
        chart-data (->> report-data
                        (map second))
        labels (->> report-data
                    (map first))
        view-name (loc/tr :number-of-deals-per-stage-custom-alj)]
    [alj-chart
     {:view-name     view-name
      :total-deals   (count active-deals)
      :report-data   report-data
      :chart-data    chart-data
      :x-axis-labels labels}]))

(defn active-deals-per-deal-type
  []
  (let [active-deals (active-deals)
        report-data
        (multi-named-value-counts-report active-deals :oppo/tag-deal-type (alj-report-pipeline-id))
        chart-data (->> report-data
                        (map second))
        labels (->> report-data
                    (map first))
        view-name (loc/tr :number-of-deals-per-tag-deal-type-custom-alj)]
    [alj-chart
     {:view-name     view-name
      :total-deals   (count active-deals)
      :report-data   report-data
      :chart-data    chart-data
      :x-axis-labels labels}]))

(defn active-deals-per-business-units
  []
  (let [active-deals (active-deals)
        report-data (multi-named-value-counts-report active-deals
                                                     :oppo/group-business-units
                                                     (alj-report-pipeline-id))
        chart-data (->> report-data
                        (map second))
        labels (->> report-data
                    (map first))
        view-name (loc/tr :number-of-deals-per-group-business-unit-custom-alj)]
    [alj-chart
     {:view-name     view-name
      :total-deals   (count active-deals)
      :report-data   report-data
      :chart-data    chart-data
      :x-axis-labels labels}]))

(defn active-deals-per-target-asset-industry
  []
  (let [active-deals (active-deals)
        active-target-assets (->> active-deals
                                  (keep (fn [oppo]
                                          (->> oppo
                                               :oppo/target-asset
                                               (get @session/entities)))))
        report-data (multi-named-value-counts-report active-target-assets
                                                     :asset/tag-industry
                                                     (alj-report-pipeline-id))
        chart-data (->> report-data
                        (map second))
        labels (->> report-data
                    (map first))
        view-name (loc/tr :number-of-deals-per-target-asset-tag-industry-custom-alj)]
    [alj-chart
     {:view-name     view-name
      :total-deals   (count active-deals)
      :report-data   report-data
      :chart-data    chart-data
      :x-axis-labels labels}]))

(defn bu-active-deals-per-stage
  [business-units-by-name bu-name]
  (let [bu (get business-units-by-name bu-name)
        active-deals (active-deals)
        active-bu-deals (->> active-deals
                             (filter (fn [o]
                                       (some #{(:db/id bu)} (:oppo/group-business-units o)))))
        report-data (deals-per-stages-no-zeros active-bu-deals)
        view-name (loc/tr :number-of-active-deals-per-stage-bu-custom-alj [bu-name])]
    (if-not bu
      [:div
       (loc/tr :not-found- [(str "'" bu-name "' - business unit")])]
      [alj-table
       {:view-name  view-name
        :header-row [(loc/tr :oppo/stage) bu-name]
        :data-rows  report-data
        :footer-row [(loc/tr :total)
                     (->> report-data
                          (keep second)
                          (cu/sum-ignore-non-numbers))]}])))

(defn menax-all-deals-per-stage-this-year
  [start-of-this-year-ms all-bus-by-name]
  (let [menax-bu (get all-bus-by-name "Menax")
        all-menax-deals (->> (all-deals)
                             (filter (fn [o]
                                       (some #{(:db/id menax-bu)} (:oppo/group-business-units o))))
                             (filter (fn [o]
                                       (> (:common/created-on o) start-of-this-year-ms))))
        report-data (deals-per-stages-no-zeros all-menax-deals)
        view-name (loc/tr :number-of-deals-per-stage-menax-bu-custom-alj [(.getFullYear (js/Date.))])]
    (if-not menax-bu
      [:div
       (loc/tr :not-found- ["'Menax' - business unit"])]
      [alj-table
       {:view-name  view-name
        :header-row [(loc/tr :oppo/stage) (:named-value/value menax-bu)]
        :data-rows  report-data
        :footer-row [(loc/tr :total)
                     (->> report-data
                          (keep second)
                          (cu/sum-ignore-non-numbers))]}])))

(defn bu-active-deals-per-segment
  [business-units-by-name bu-name]
  (let [bu (get business-units-by-name bu-name)
        active-deals (active-deals)
        active-bu-deals (->> active-deals
                             (filter (fn [o]
                                       (some #{(:db/id bu)} (:oppo/group-business-units o)))))
        report-data (->> (multi-named-value-counts-report active-bu-deals
                                                          :oppo/tag-segment
                                                          (alj-report-pipeline-id))
                         (replace-zeros))
        view-name (loc/tr :number-of-active-deals-per-segment-bu-custom-alj [bu-name])]
    (if-not bu
      [:div
       (loc/tr :not-found- [(str "'" bu-name "' - business unit")])]
      [alj-table
       {:view-name  view-name
        :header-row [(loc/tr :oppo/tag-segment) bu-name]
        :data-rows  report-data
        :footer-row [(loc/tr :total)
                     (->> report-data
                          (keep second)
                          (cu/sum-ignore-non-numbers))]}])))

(defn bu-active-deals-per-deal-type
  [business-units-by-name bu-name]
  (let [bu (get business-units-by-name bu-name)
        active-deals (active-deals)
        active-bu-deals (->> active-deals
                             (filter (fn [o]
                                       (some #{(:db/id bu)} (:oppo/group-business-units o)))))
        report-data (->> (multi-named-value-counts-report active-bu-deals
                                                          :oppo/tag-deal-type
                                                          (alj-report-pipeline-id))
                         (replace-zeros))
        view-name (loc/tr :number-of-active-deals-per-deal-type-bu-custom-alj [bu-name])]
    (if-not bu
      [:div
       (loc/tr :not-found- [(str "'" bu-name "' - business unit")])]
      [alj-table
       {:view-name  view-name
        :header-row [(loc/tr :oppo/tag-deal-type) bu-name]
        :data-rows  report-data
        :footer-row [(loc/tr :total)
                     (->> report-data
                          (keep second)
                          (cu/sum-ignore-non-numbers))]}])))


(defn menax-all-deals-per-segment-this-year
  [start-of-this-year-ms all-bus-by-name]
  (let [menax-bu (get all-bus-by-name "Menax")
        all-menax-deals (->> (all-deals)
                             (filter (fn [o]
                                       (some #{(:db/id menax-bu)} (:oppo/group-business-units o))))
                             (filter (fn [o]
                                       (> (:common/created-on o) start-of-this-year-ms))))
        report-data (->> (multi-named-value-counts-report all-menax-deals
                                                          :oppo/tag-segment
                                                          (alj-report-pipeline-id))
                         (replace-zeros))
        view-name (loc/tr :number-of-deals-per-segment-menax-bu-custom-alj [(.getFullYear (js/Date.))])]
    (if-not menax-bu
      [:div
       (loc/tr :not-found- ["'Menax' - business unit"])]
      [alj-table
       {:view-name  view-name
        :header-row [(loc/tr :oppo/tag-segment) (:named-value/value menax-bu)]
        :data-rows  report-data
        :footer-row [(loc/tr :total)
                     (->> report-data
                          (keep second)
                          (cu/sum-ignore-non-numbers))]}])))

(defn jimco-report
  [{:keys [HCF GTF view-name export-data main-heading
           table-header table-footer
           HCF-header GTF-header
           report-data-HCF report-data-GTF]}]
  (let [HCF-name (:named-value/value HCF)
        GTF-name (:named-value/value GTF)]
    (if-not (and HCF GTF)
      (loc/tr :not-found- [(str "'" HCF-name "' or '" GTF-name "' - business unit")])
      [card/card
       [card/header
        [:div.d-flex.align-items-center
         [:span.title-h3.d-inline-block.pr-2 view-name]]

        [:div.d-flex.justify-content-end
         [:i.fal.fa-copy.mr-3.pointer
          {:title    (loc/tr :copy-to-clipboard)
           :on-click (fn [_]
                       (clipboard/copy-as-tsv export-data))}]
         [:i.fal.fa-file-excel.pointer
          {:title    (loc/tr :save-as-excel)
           :on-click (fn [_]
                       (excel/export-array-data
                        view-name
                        export-data))}]]]
       [card/body
        [:table.zap-simple-table.zap-simple-table--md.w-100
         [:thead
          [:tr
           (->> table-header
                (map-indexed (fn [idx heading]
                               ^{:key (str view-name "-main-heading-" heading "-" idx)}
                               [:th heading]))
                (doall))]
          [:tr
           (->> GTF-header
                (map-indexed (fn [idx heading]
                               ^{:key (str view-name "-heading-" heading "-" idx)}
                               [:th.bgr-light-grey heading]))
                (doall))]]
         [:tbody
          {:key (str view-name "-body")}
          (->> report-data-GTF
               (map-indexed
                (fn [row-idx row-data]
                  [:tr
                   {:key (str view-name "-row-" row-idx)}
                   (->> row-data
                        (map-indexed (fn [idx cell-data]
                                       [:td
                                        {:key        (str view-name "-" row-idx "-" idx)
                                         :class-name (if (= 0 idx) "string-value" "number-value")}
                                        cell-data]))
                        (doall))]))
               (doall))]
         [:thead
          [:tr
           (->> HCF-header
                (map-indexed (fn [idx heading]
                               ^{:key (str view-name "-2-heading-" heading "-" idx)}
                               [:th.bgr-light-grey heading]))
                (doall))]]
         [:tbody
          {:key (str view-name "-2-body-")}
          (->> report-data-HCF
               (map-indexed
                (fn [row-idx row-data]
                  [:tr
                   {:key (str view-name "-2-row-" row-idx)}
                   (->> row-data
                        (map-indexed (fn [idx cell-data]
                                       [:td
                                        {:key        (str view-name "-2-" row-idx "-" idx)
                                         :class-name (if (= 0 idx) "string-value" "number-value")}
                                        cell-data]))
                        (doall))]))
               (doall))]
         [:tfoot
          [:tr
           (->> table-footer
                (map-indexed (fn [idx footer-cell-data]
                               [:td
                                {:key (str view-name "-footer-" idx)}
                                footer-cell-data]))
                (doall))]]]]])))

(defn jimco-active-deals-per-stage
  [business-units-by-name GTF-name HCF-name]
  (let [GTF (get business-units-by-name GTF-name)
        HCF (get business-units-by-name HCF-name)
        active-deals (active-deals)
        active-GTF-deals (->> active-deals
                              (filter (fn [o]
                                        (some #{(:db/id GTF)} (:oppo/group-business-units o)))))
        active-HCF-deals (->> active-deals
                              (filter (fn [o]
                                        (some #{(:db/id HCF)} (:oppo/group-business-units o)))))
        report-data-GTF (deals-per-stages-no-zeros active-GTF-deals)
        GTF-total (->> report-data-GTF
                       (keep second)
                       (cu/sum-ignore-non-numbers))
        report-data-HCF (deals-per-stages-no-zeros active-HCF-deals)
        HCF-total (->> report-data-HCF
                       (keep second)
                       (cu/sum-ignore-non-numbers))
        view-name (loc/tr :number-of-active-deals-per-stage-jimco-custom-alj [GTF-name HCF-name])
        main-heading ["SOP Stage"]
        table-header [(loc/tr :oppo/group-business-units) (loc/tr :deals)]
        GTF-header [GTF-name GTF-total]
        HCF-header [HCF-name HCF-total]
        table-footer [(loc/tr :grand-total) (+ GTF-total HCF-total)]
        export-data (concat [main-heading]
                            [table-header]
                            [GTF-header]
                            report-data-GTF
                            [HCF-header]
                            report-data-HCF
                            [table-footer])]
    [jimco-report
     {:HCF             HCF
      :GTF             GTF
      :view-name       view-name
      :export-data     export-data
      :main-heading    main-heading
      :table-header    table-header
      :table-footer    table-footer
      :HCF-header      HCF-header
      :GTF-header      GTF-header
      :report-data-HCF report-data-HCF
      :report-data-GTF report-data-GTF}]))

(defn jimco-active-deals-per-deal-type
  [business-units-by-name GTF-name HCF-name]
  (let [GTF (get business-units-by-name GTF-name)
        HCF (get business-units-by-name HCF-name)
        active-deals (active-deals)
        active-GTF-deals (->> active-deals
                              (filter (fn [o]
                                        (some #{(:db/id GTF)} (:oppo/group-business-units o)))))
        active-HCF-deals (->> active-deals
                              (filter (fn [o]
                                        (some #{(:db/id HCF)} (:oppo/group-business-units o)))))
        report-data-GTF (->> (multi-named-value-counts-report active-GTF-deals
                                                              :oppo/tag-deal-type
                                                              (alj-report-pipeline-id))
                             (replace-zeros))
        GTF-total (->> report-data-GTF
                       (keep second)
                       (cu/sum-ignore-non-numbers))
        report-data-HCF (->> (multi-named-value-counts-report active-HCF-deals
                                                              :oppo/tag-deal-type
                                                              (alj-report-pipeline-id))
                             (replace-zeros))
        HCF-total (->> report-data-HCF
                       (keep second)
                       (cu/sum-ignore-non-numbers))
        view-name (loc/tr :number-of-active-deals-per-deal-type-jimco-custom-alj [GTF-name HCF-name])
        main-heading [(loc/tr :oppo/tag-deal-type)]
        table-header [(loc/tr :oppo/group-business-units) (loc/tr :deals)]
        GTF-header [GTF-name GTF-total]
        HCF-header [HCF-name HCF-total]
        table-footer [(loc/tr :grand-total) (+ GTF-total HCF-total)]
        export-data (concat [main-heading]
                            [table-header]
                            [GTF-header]
                            report-data-GTF
                            [HCF-header]
                            report-data-HCF
                            [table-footer])]
    [jimco-report
     {:HCF             HCF
      :GTF             GTF
      :view-name       view-name
      :export-data     export-data
      :main-heading    main-heading
      :table-header    table-header
      :table-footer    table-footer
      :HCF-header      HCF-header
      :GTF-header      GTF-header
      :report-data-HCF report-data-HCF
      :report-data-GTF report-data-GTF}]))

(defn view
  [_]
  (let [start-of-this-year-ms (-> (js/Date.)
                                  (.getFullYear)
                                  (js/Date. 0 1)
                                  (.getTime))]
    (r/create-class
     {:reagent-render
      (fn [_]
        (let [business-units (session/non-archived-named-values :oppo/group-business-units
                                                                (alj-report-pipeline-id))
              bu-by-name (->> business-units
                              (p/index-by :named-value/value))
              menax-bu-name "MENAX"
              alj-health-bu-name "ALJ Health"
              alj-motors-bu-name "ALJ Mobility"
              frv-bu-name "FRV"
              GTF-bu-name "JIMCO General Tech Fund"
              HCF-bu-name "JIMCO Healthcare Fund"]
          [:div
           {:class "grid grid-cols-2 gap-4"}
           [:div
            [active-deals-per-stage]]
           [:div
            [active-deals-per-deal-type]]
           [:div
            [active-deals-per-business-units]]
           [:div
            [active-deals-per-target-asset-industry]]

           [:div
            [bu-active-deals-per-stage bu-by-name menax-bu-name]]
           [:div
            [bu-active-deals-per-segment bu-by-name menax-bu-name]]

           [:div
            [menax-all-deals-per-stage-this-year start-of-this-year-ms bu-by-name]]
           [:div
            [menax-all-deals-per-segment-this-year start-of-this-year-ms bu-by-name]]

           [:div
            [bu-active-deals-per-stage bu-by-name alj-health-bu-name]]
           [:div
            [bu-active-deals-per-segment bu-by-name alj-health-bu-name]]
           [:div
            [bu-active-deals-per-deal-type bu-by-name alj-health-bu-name]]
           [:div]

           [:div
            [bu-active-deals-per-stage bu-by-name alj-motors-bu-name]]
           [:div
            [bu-active-deals-per-segment bu-by-name alj-motors-bu-name]]
           [:div
            [bu-active-deals-per-deal-type bu-by-name alj-motors-bu-name]]
           [:div]

           [:div
            [bu-active-deals-per-stage bu-by-name frv-bu-name]]
           [:div
            [bu-active-deals-per-segment bu-by-name frv-bu-name]]
           [:div
            [bu-active-deals-per-deal-type bu-by-name frv-bu-name]]
           [:div]

           [:div
            [jimco-active-deals-per-stage bu-by-name GTF-bu-name HCF-bu-name]]
           [:div
            [jimco-active-deals-per-deal-type bu-by-name GTF-bu-name HCF-bu-name]]]))})))
